import { useState, type FormEvent } from "react";
import { C, font } from "../theme";
import { ApogeuLogo } from "../components/ApogeuLogo";
import { FieldInput } from "../components/Field";
import { Button } from "../components/Button";
import { Select } from "../components/Select";
import { useSession } from "../lib/session/SessionContext";
import { acceptInvite } from "../lib/api/endpoints";
import { ApiError } from "../lib/api/client";
import type { AcceptInviteResult } from "../types";

interface OnboardingCtScreenProps {
  code: string;
  onAccepted: (result: AcceptInviteResult) => void;
}

const TIMEZONES = [
  { value: "America/Sao_Paulo", label: "America/Sao_Paulo (UTC-3)" },
  { value: "America/Manaus", label: "America/Manaus (UTC-4)" },
  { value: "America/Belem", label: "America/Belem (UTC-3)" },
  { value: "America/Recife", label: "America/Recife (UTC-3)" },
  { value: "America/Fortaleza", label: "America/Fortaleza (UTC-3)" },
  { value: "America/Bahia", label: "America/Bahia (UTC-3)" },
  { value: "America/Cuiaba", label: "America/Cuiaba (UTC-4)" },
  { value: "America/Porto_Velho", label: "America/Porto_Velho (UTC-4)" },
  { value: "America/Boa_Vista", label: "America/Boa_Vista (UTC-4)" },
  { value: "America/Rio_Branco", label: "America/Rio_Branco (UTC-5)" },
];

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function OnboardingCtScreen({ code, onAccepted }: OnboardingCtScreenProps) {
  const { accessToken } = useSession();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [document, setDocument] = useState("");
  const [timeZoneId, setTimeZoneId] = useState("America/Sao_Paulo");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onNameChange = (value: string) => {
    setName(value);
    if (!slug || slug === slugify(name)) setSlug(slugify(value));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name || !slug || !document) {
      setError("Preencha nome, slug e documento do CT.");
      return;
    }
    if (!accessToken) return;
    setBusy(true);
    try {
      const result = await acceptInvite(accessToken, code, { name, slug, document, timeZoneId });
      onAccepted(result);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409 && err.extensions?.code === "INVITE_NOT_PENDING") setError("Este convite já foi usado ou não está mais pendente.");
        else if (err.status === 403 && err.extensions?.code === "INVITE_EMAIL_MISMATCH") setError("O e-mail autenticado não corresponde ao e-mail convidado.");
        else if (err.status === 409 && err.extensions?.code === "SLUG_IN_USE") setError("Este slug já está em uso. Escolha outro.");
        else if (err.status === 400) setError("Dados inválidos. Revise nome, slug, documento e timezone.");
        else setError(err.detail ?? "Não foi possível aceitar o convite.");
      } else {
        setError(err instanceof Error ? err.message : "Não foi possível aceitar o convite.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: C.white,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        fontFamily: font,
      }}
    >
      <ApogeuLogo width={150} />
      <div style={{ fontSize: 12, letterSpacing: 4, color: C.light, marginTop: 12, marginBottom: 28 }}>
        MONTE O SEU CT
      </div>
      <div style={{ fontSize: 13, color: C.gray, maxWidth: 360, lineHeight: 1.6, textAlign: "center", marginBottom: 18 }}>
        Você é o responsável por este centro de treinamento. Preencha os dados iniciais para criar o CT.
      </div>
      <form
        onSubmit={submit}
        style={{
          width: "100%",
          maxWidth: 320,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <label style={{ fontSize: 11, letterSpacing: 1, color: C.gray, fontWeight: 600 }}>
          NOME DO CT
          <FieldInput type="text" value={name} onChange={e => onNameChange(e.target.value)} placeholder="Arena Norte" style={{ marginTop: 4 }} />
        </label>
        <label style={{ fontSize: 11, letterSpacing: 1, color: C.gray, fontWeight: 600 }}>
          SLUG
          <FieldInput type="text" value={slug} onChange={e => setSlug(slugify(e.target.value))} placeholder="arena-norte" style={{ marginTop: 4 }} />
        </label>
        <label style={{ fontSize: 11, letterSpacing: 1, color: C.gray, fontWeight: 600 }}>
          DOCUMENTO (CPF/CNPJ)
          <FieldInput type="text" value={document} onChange={e => setDocument(e.target.value)} placeholder="000.000.000-00" style={{ marginTop: 4 }} />
        </label>
        <label style={{ fontSize: 11, letterSpacing: 1, color: C.gray, fontWeight: 600 }}>
          TIMEZONE
          <Select id="timezone" value={timeZoneId} options={TIMEZONES} onChange={setTimeZoneId} style={{ marginTop: 4 }} ariaLabel="Timezone do CT" />
        </label>
        {error && (
          <div style={{ fontSize: 12.5, color: C.red, background: "C.redSoft", borderRadius: 8, padding: "8px 12px" }}>
            {error}
          </div>
        )}
        <Button type="submit" disabled={busy} style={{ fontSize: 13, padding: "11px 14px" }}>
          {busy ? "Criando CT..." : "Criar CT e continuar"}
        </Button>
      </form>
    </div>
  );
}